import type { Permission } from "../types/auth";
import { apiClient } from "./apiClient";

// Interfaces para tipado
interface CreatePermissionDto {
  nombre: string;
  descripcion: string;
  estructura_permisos: Record<string, any>;
  activo?: boolean;
}

interface UpdatePermissionDto {
  nombre?: string;
  descripcion?: string;
  estructura_permisos?: Record<string, any>;
  activo?: boolean;
}

/**
 * Servicio para gestión de permisos
 * Utiliza apiClient con interceptores automáticos para autenticación
 * Todas las requests incluyen automáticamente el token JWT
 */
class PermissionService {
  /**
   * Obtener todos los permisos
   */
  async getAllPermissions(): Promise<Permission[]> {
    try {
      const response = await apiClient.get<{
        permissions: Permission[];
        total: number;
        page: number;
        limit: number;
      }>("/permissions");
      return response.data.permissions;
    } catch (error) {
      console.error("Error obteniendo permisos:", error);
      throw new Error("No se pudieron cargar los permisos");
    }
  }

  /**
   * Obtener permisos activos únicamente
   */
  async getActivePermissions(): Promise<Permission[]> {
    try {
      const response = await apiClient.get<{
        permissions: Permission[];
        total: number;
        page: number;
        limit: number;
      }>("/permissions?activo=true");
      return response.data.permissions;
    } catch (error) {
      console.error("Error obteniendo permisos activos:", error);
      throw new Error("No se pudieron cargar los permisos activos");
    }
  }

  /**
   * Obtener permiso por ID
   */
  async getPermissionById(id: string): Promise<Permission> {
    try {
      const response = await apiClient.get<Permission>(`/permissions/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo permiso:", error);
      throw new Error("No se pudo cargar el permiso");
    }
  }

  /**
   * Crear nuevo permiso
   */
  async createPermission(
    permissionData: CreatePermissionDto,
  ): Promise<Permission> {
    try {
      const response = await apiClient.post<Permission>(
        "/permissions",
        permissionData,
      );
      return response.data;
    } catch (error) {
      console.error("Error creando permiso:", error);
      throw new Error("No se pudo crear el permiso");
    }
  }

  /**
   * Actualizar permiso existente
   */
  async updatePermission(
    id: string,
    permissionData: UpdatePermissionDto,
  ): Promise<Permission> {
    try {
      const response = await apiClient.patch<Permission>(
        `/permissions/${id}`,
        permissionData,
      );
      return response.data;
    } catch (error) {
      console.error("Error actualizando permiso:", error);
      throw new Error("No se pudo actualizar el permiso");
    }
  }

  /**
   * Eliminar permiso
   */
  async deletePermission(id: string): Promise<void> {
    try {
      await apiClient.delete(`/permissions/${id}`);
    } catch (error) {
      console.error("Error eliminando permiso:", error);
      throw new Error("No se pudo eliminar el permiso");
    }
  }

  /**
   * Activar/Desactivar permiso
   */
  async togglePermissionStatus(
    id: string,
    activo: boolean,
  ): Promise<Permission> {
    try {
      const response = await apiClient.patch<Permission>(
        `/permissions/${id}/status`,
        { activo },
      );
      return response.data;
    } catch (error) {
      console.error("Error cambiando estado del permiso:", error);
      throw new Error("No se pudo cambiar el estado del permiso");
    }
  }

  /**
   * Validar si el usuario actual tiene un permiso específico
   */
  async validatePermission(
    ruta: string,
    menu: string,
    funcion: string,
  ): Promise<boolean> {
    try {
      const response = await apiClient.get<{ hasPermission: boolean }>(
        "/auth/validate-permission",
        {
          params: { ruta, menu, funcion },
        },
      );
      return response.data.hasPermission;
    } catch (error) {
      console.error("Error validando permiso:", error);
      return false; // En caso de error, denegar el permiso por seguridad
    }
  }

  /**
   * Obtener permisos de un grupo específico
   */
  async getGroupPermissions(groupId: string): Promise<Permission[]> {
    try {
      const response = await apiClient.get<Permission[]>(
        `/groups/${groupId}/permissions`,
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo permisos del grupo:", error);
      throw new Error("No se pudieron cargar los permisos del grupo");
    }
  }

  /**
   * Asignar permisos a un grupo
   */
  async assignPermissionsToGroup(
    groupId: string,
    permissionIds: string[],
  ): Promise<void> {
    try {
      await apiClient.patch(`/groups/${groupId}/permissions`, {
        permissionIds,
      });
    } catch (error) {
      console.error("Error asignando permisos al grupo:", error);
      throw new Error("No se pudieron asignar los permisos al grupo");
    }
  }
}

// Exportar instancia única del servicio
export const permissionService = new PermissionService();
export type { CreatePermissionDto, UpdatePermissionDto };
